import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import SplashScreen from './SplashScreen';
import LoginPage from './LoginPage';
import RegistrationPage from './RegistrationPage';
import PrivateRoute from './PrivateRoute'; // Protect certain routes
import MyDashboard from './MyDashboard'; // Dashboard component
import MyCalendar from './MyCalendar'; // Calendar component
import CreateEvent from './CreateEvent'; // Import the CreateEvent component
import Logout from './Logout'; // Add logout functionality if needed
import CreateClient from './CreateClient'; // Import the new component
import Clients from './Clients'; // Import the Clients component
import RealTimeTranscription from './RealTimeTranscription'; // Adjust the path if necessary
import ClientDashboard from './ClientDashboard';
import Settings from './Settings';
import ClientBuild from './ClientBuild';
import VideoConference from './VideoConference'; // Ensure you have the VideoConference component imported
import HostConference from './HostConference';
import GuestConference from './GuestConference';
import PublicCalendar from './PublicCalendar'; // Import the PublicCalendar component
import PasswordResetRequestPage from './PasswordResetRequestPage';
import ResetPasswordPage from './ResetPasswordPage';
import OAuthCallback from './OAuthCallback';
import PublicSurvey from './PublicSurvey'; // Adjust the import path as needed
import CreateCircle from './CreateCircle';
import ManageCircle from './ManageCircle';
import JoinCircle from './JoinCircle';
import CircleClients from './CircleClients';
import HelpPage from './HelpPage';
import CircleCalendar from './CircleCalendar';
import BillingPage from './BillingPage';
import ConsentPage from './ConsentPage';


// NEW: AI Persona imports
import PersonaSetup from './PersonaSetup';
import PublicPersonaChat from './PublicPersonaChat';
import PersonaConversationView from './PersonaConversationView';
import Pricing from './Pricing';

function App() {
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    document.documentElement.classList.add(platform);

    if (platform === 'android') {
      // Get the exact status bar height and apply it as a CSS variable
      StatusBar.setStyle({ style: Style.Light });
      StatusBar.getInfo().then(info => {
        // info.overlays tells us if the webview is under the status bar
        // Use window.screen to calculate status bar height
      });

      // Android status bar is typically 24dp but varies — read it from the window
      const statusBarHeight = window.screen.height - window.screen.availHeight;
      if (statusBarHeight > 0) {
        document.documentElement.style.setProperty('--status-bar-height', `${statusBarHeight}px`);
      } else {
        // Fallback: use a safe default for Android
        document.documentElement.style.setProperty('--status-bar-height', '48px');
      }
    } else {
      document.documentElement.style.setProperty('--status-bar-height', '0px');
    }
  }, []);

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegistrationPage />} />

        {/* Protected routes */}
        <Route path="/dashboard" element={<PrivateRoute><MyDashboard /></PrivateRoute>} />
        <Route path="/realtimetranscription" element={<RealTimeTranscription />} />
        <Route path="/calendar" element={<PrivateRoute><MyCalendar /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/logout" element={<PrivateRoute><Logout /></PrivateRoute>} />
        <Route path="/events/new" element={<PrivateRoute><CreateEvent /></PrivateRoute>} /> {/* New event creation route */}
        <Route path="/createclient" element={<PrivateRoute><CreateClient /></PrivateRoute>} />
        <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
        <Route path="/clientbuild" element={<PrivateRoute><ClientBuild /></PrivateRoute>} />
        <Route path="/VideoConference" element={<VideoConference />} /> {/* Add this line */}
        <Route path="/host-conference" element={<HostConference />} />
        <Route path="/guest-conference" element={<GuestConference />} />
        <Route path="/clientdashboard/:clientId" element={<PrivateRoute><ClientDashboard /></PrivateRoute>} />
        <Route path="/public-calendar/:userId" element={<PublicCalendar />} />
        <Route path="/request-reset" element={<PasswordResetRequestPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />
        <Route path="/public-survey/:userId" element={<PublicSurvey />} />
        <Route path="/circles/:id/clients" element={<CircleClients />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/circles/:id/calendar" element={<CircleCalendar />} />
        <Route path="/consent/:token" element={<ConsentPage />} />
        <Route path="/circles/create" element={<CreateCircle />} />
        <Route path="/circles/join" element={<JoinCircle />} />
        <Route path="/circles/:id/manage" element={<ManageCircle />} />
        <Route path="/dashboard/persona/conversations/:conversationId" element={<PrivateRoute><PersonaConversationView /></PrivateRoute>} />

        {/* NEW: AI Persona routes */}
        <Route path="/persona" element={<PrivateRoute><PersonaSetup /></PrivateRoute>} />
        <Route path="/persona/conversation/:conversationId" element={<PrivateRoute><PersonaConversationView /></PrivateRoute>} />
        <Route path="/ai/:token" element={<PublicPersonaChat />} /> {/* Public client chat page */}

        <Route path="/billing" element={<BillingPage />} />

        {/* Pricing / Upgrade */}
        <Route path="/pricing" element={<PrivateRoute><Pricing /></PrivateRoute>} />

        {/* Splash screen */}
        <Route path="/splash" element={<SplashScreen />} />

        {/* Default route — show splash on native, login on web */}
        <Route path="/" element={<SplashScreen />} />
      </Routes>
    </Router>
  );
}

export default App;